/**
 * SQL 预览 Hook
 *
 * 负责请求编排与弹窗状态；具体接口与入参由调用方通过 fetchPreviewSql 注入。
 */

import { useState, useCallback } from 'react';
import { notification } from 'tea-component';
import type { IPreviewSqlVo } from '@dbl/services/dataSource';

import { formatPreviewSql } from './previewSqlUtils';
import type { SqlLanguage } from 'sql-formatter';

export interface IUsePreviewSQLOptions {
  /** 由调用方注入：分析页 getPreviewSQL，报表页 previewDashboardChartSql */
  fetchPreviewSql: () => Promise<IPreviewSqlVo>;
  /** SQL 格式化方言，默认 mysql */
  sqlFormatLanguage?: SqlLanguage;
}

export interface IUsePreviewSQLResult {
  /** 预览 SQL 函数 */
  previewSQL: () => Promise<void>;
  /** 请求加载中 */
  loading: boolean;
  /** 请求失败 */
  error: boolean;
  /** 弹窗可见性 */
  modalVisible: boolean;
  /** 显示弹窗 */
  showModal: () => void;
  /** 隐藏弹窗 */
  hideModal: () => void;
  /** 当前 SQL 内容 */
  sqlContent: string;
}

/**
 * SQL 预览 Hook
 *
 * @param options 配置选项
 * @returns previewSQL 函数、loading 状态和弹窗控制方法
 */
export const usePreviewSQL = (
  options: IUsePreviewSQLOptions,
): IUsePreviewSQLResult => {
  const { fetchPreviewSql, sqlFormatLanguage = 'mysql' } = options;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [sqlContent, setSqlContent] = useState<string>('');

  const previewSQL = useCallback(async (): Promise<void> => {
    setModalVisible(true);
    setLoading(true);
    setError(false);
    setSqlContent('');

    try {
      const { finalSql } = await fetchPreviewSql();

      if (!finalSql) {
        setModalVisible(false);
        notification.warning({
          description: 'SQL 预览内容为空',
          duration: 3000,
        });
        return;
      }

      setSqlContent(formatPreviewSql(finalSql, sqlFormatLanguage));
    } catch (err) {
      console.error('获取 SQL 预览失败:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [fetchPreviewSql, sqlFormatLanguage]);

  const showModal = useCallback((): void => {
    setModalVisible(true);
  }, []);

  const hideModal = useCallback((): void => {
    setModalVisible(false);
    setLoading(false);
    setError(false);
  }, []);

  return {
    previewSQL,
    loading,
    error,
    modalVisible,
    showModal,
    hideModal,
    sqlContent,
  };
};
